import { toNumber } from "lodash";
import * as React from "react";
import Collapsible from "react-collapsible";
import { FormattedMessage, InjectedIntlProps, injectIntl } from "react-intl";
import { Link } from "react-router-dom";
import * as ReactTagsInput from "react-tagsinput";
import { preferredEditor } from "src/constants/profileSettings";
import { Log, logAction, logType } from "src/utils/api";
import { shortenTo } from 'src/utils/stringManipulation';
import GeneratorEntity from "../../../models/Generator";
import StandardImage from "./../../../assets/cards/standard.png";
import { history } from "./../../../store/store";
import { AreaInput } from "./input/AreaInput";
import { TextInput } from "./input/TextInput";
import { TextInputSmall } from "./input/TextInputSmall";
import translations from "./translations";

interface IState {
  currentPages: {},
  dom: boolean;
  elements: any;
  entriesPerPage: number;
  id: string;
  isLoadingSaving: string;
  isNew: boolean;
  searchEntryStrings: {};
  tags: any;
}

interface IProps extends InjectedIntlProps {
  currentGen: GeneratorEntity;
  generator: GeneratorEntity;
  errorMessage: string
  id: string;
  theme: string;
  validationError: boolean;
  toggleRunView: () => void;
  loadGeneratorDetails: (id: string) => void;
  isLoadingValidation: boolean;
  isShowingRunWindow: boolean
  receivedUpdate: boolean;
  syntaxError: boolean;
  changeLanguage: () => void;
  contentChanged: (value: string) => void;
  refresh: () => void;
  runGenerator: (code: string) => void;
  saveGenerator: () => void;
  setGen: () => void;
  switchView: (view: string) => void;
  validateGenerator: () => void;
  validated: boolean;
}

class FormGeneratorComponent extends React.Component<IProps, IState> {

  public elementCounter = 1;
  public obj = {};
  public pageNumber = {};
  public tableLengthsUpdated = false;

  public emptyTable = {
    "entries": [
      {
        "m": 1,
        "v": "default value"
      }
    ],
    "name": "new table"
  }

  public emptyRow = {
    export: '',
    m: 1,
    v: 'default value',
  }

  public emptySet = {
    ['new-0']: {
      v: 'value'
    }
  }

  public addEmptySet = {
    v: 'value'
  }

  public emptyGlobal = {
    ['enew-0']: {
      v: 'value'
    }
  }

  public addEmptyGlobal = {
    v: 'value'
  }

  public constructor(props: IProps) {
    super(props);
    let newGenerator = false;
    if (props.id === "new" && window.location.href.endsWith("/new")) {
      newGenerator = true;
    }

    this.state = {
      currentPages: {},
      dom: false,
      elements: [],
      entriesPerPage: 50,
      id: props.id,
      isLoadingSaving: "",
      isNew: newGenerator,
      searchEntryStrings: {},
      tags: []
    };

    this.handleTagChange = this.handleTagChange.bind(this);
  }

  public componentDidMount() {
    if ((this.props.generator.id === "new" && this.props.receivedUpdate === false)) {
      this.props.setGen()
      this.tableLengthsUpdated = false;
    }
  }

  public componentDidUpdate() {
    if (this.props.generator.hasAllData === true && this.props.receivedUpdate === false) {
      this.props.setGen()
    }
    if (!this.tableLengthsUpdated) {
      this.updateTableLengths();
    }
  }

  public componentWillUnmount() {
    // window.console.info("unmounting form");
  }

  public handleTagChange(tags: any) {
    const newGen = { ...this.props.currentGen, categories: tags }
    this.props.contentChanged(JSON.stringify(newGen))
  }

  public goBack = () => {
    history.push("/");
  }

  public changeHandler = (e: React.FormEvent<any>, field: string) => {
    const gen = { ...this.props.currentGen };
    const value = e.currentTarget.value;
    switch (field) {
      case "name":
        gen.name = value;
        break;
      case "explanation":
        gen.explanation = value;
        break;
      case "path":
        gen.path = value;
        break;
      case "result-pattern":
        gen.resultPattern = value;
        break;
      default:
        break;
    }
    gen.isDirty = true;
    this.props.contentChanged(JSON.stringify(gen));
  }

  public tableChange = (e: React.FormEvent<any>, type: string, tableCount: number, count?: number, lineCount?: number) => {
    const tempTables = this.props.currentGen.tables
    let newGen = {}
    switch (type) {
      case 'tableName':
        tempTables[tableCount].name = e.currentTarget.value
        newGen = {
          ...this.props.currentGen,
          tables: tempTables
        }
        this.props.contentChanged(JSON.stringify(newGen))
        break
      case 'tableExplanation':
        tempTables[tableCount].explanation = e.currentTarget.value
        newGen = {
          ...this.props.currentGen,
          tables: tempTables
        }
        this.props.contentChanged(JSON.stringify(newGen))
        break
      case 'tableGlobalKey':
        const expo = tempTables[tableCount].export
        let newExport = {}
        let counter = 0
        let value = ''
        for (const i in expo) {// tslint:disable-line
          if (counter === count!) {
            value = expo[i].v
            newExport = {
              ...newExport,
              [e.currentTarget.value]: { v: value }
            }
          } else {
            newExport = {
              ...newExport,
              [i]: expo[i]
            }
          }
          counter++
        }
        tempTables[tableCount].export = newExport
        newGen = {
          ...this.props.currentGen,
          tables: tempTables
        }
        this.props.contentChanged(JSON.stringify(newGen))
        break

      case 'rowSetKey':
        const oldSet = tempTables[tableCount].entries[lineCount!].set
        let newSet = {}
        let cntr = 0
        let val = ''
        for (const i in oldSet) {// tslint:disable-line
          if (cntr === count!) {
            val = oldSet[i].v
            newSet = {
              ...newSet,
              [e.currentTarget.value]: { v: val }
            }
          } else {
            newSet = {
              ...newSet,
              [i]: oldSet[i]
            }
          }
          cntr++
        }
        tempTables[tableCount].entries[lineCount!].set = newSet
        newGen = {
          ...this.props.currentGen,
          tables: tempTables
        }
        this.props.contentChanged(JSON.stringify(newGen))
        break

      case 'rowSetValue':
        const oldSetV = tempTables[tableCount].entries[lineCount!].set
        const newSetK = Object.keys(oldSetV)[count!]
        const newSetV = {
          ...oldSetV,
          [newSetK]: { v: e.currentTarget.value }
        }
        tempTables[tableCount].entries[lineCount!].set = newSetV
        newGen = {
          ...this.props.currentGen,
          tables: tempTables
        }
        this.props.contentChanged(JSON.stringify(newGen))
        break

      case 'rowExportKey':
        const oldExpK = tempTables[tableCount].entries[lineCount!].export
        let newExpK = {}
        let cnt = 0
        let expVal = ''
        for (const i in oldExpK) {// tslint:disable-line
          if (cnt === count!) {
            expVal = oldExpK[i].v
            newExpK = {
              ...newExpK,
              [e.currentTarget.value]: { v: expVal }
            }
          } else {
            newExpK = {
              ...newExpK,
              [i]: oldExpK[i]
            }
          }
          cnt++
        }
        tempTables[tableCount].entries[lineCount!].export = newExpK
        newGen = {
          ...this.props.currentGen,
          tables: tempTables
        }
        this.props.contentChanged(JSON.stringify(newGen))
        break

      case 'rowExportValue':
        const oldExpV = tempTables[tableCount].entries[lineCount!].export
        const newKeyV = Object.keys(oldExpV)[count!]
        const newExpV = {
          ...oldExpV,
          [newKeyV]: { v: e.currentTarget.value }
        }
        tempTables[tableCount].entries[lineCount!].export = newExpV
        newGen = {
          ...this.props.currentGen,
          tables: tempTables
        }
        this.props.contentChanged(JSON.stringify(newGen))
        break

      case 'tableGlobalValue':
        const exp = tempTables[tableCount].export
        const newKey = Object.keys(exp)[count!]
        const newExp = {
          ...exp,
          [newKey]: { v: e.currentTarget.value }
        }
        tempTables[tableCount].export = newExp
        newGen = {
          ...this.props.currentGen,
          tables: tempTables
        }
        this.props.contentChanged(JSON.stringify(newGen))
        break

      case 'entryValue':
        if (typeof tempTables[tableCount].entries[count!] === 'string') {
          tempTables[tableCount].entries[count!] = { m: 1, v: tempTables[tableCount].entries[count!] };
        }
        tempTables[tableCount].entries[count!].v = e.currentTarget.value;
        newGen = {
          ...this.props.currentGen,
          tables: tempTables
        }
        this.props.contentChanged(JSON.stringify(newGen))
        break;
      case 'entryMultiplicity':
        if (typeof tempTables[tableCount].entries[count!] === 'string') {
          tempTables[tableCount].entries[count!] = { m: 1, v: tempTables[tableCount].entries[count!] };
        }
        const isNum = (e.currentTarget.value).match(/^[0-9]+$/) != null
        if (isNum) {
          tempTables[tableCount].entries[count!].m = toNumber(e.currentTarget.value);
          newGen = {
            ...this.props.currentGen,
            tables: tempTables
          }
          this.props.contentChanged(JSON.stringify(newGen))
        }
        break;
      default:
        break;
    }
  }

  public addTableGlobal(tableCount: number) {

    const newTables = this.props.currentGen.tables;

    if (newTables[tableCount].export !== undefined) { // somnethign there!
      newTables[tableCount] = {
        ...newTables[tableCount],
        export: {
          ...newTables[tableCount].export,
          ['ekey-' + Object.keys(newTables[tableCount].export).length]: this.addEmptyGlobal,
        }
      }
    } else { // no export there!
      newTables[tableCount] = {
        ...newTables[tableCount],
        export: this.emptyGlobal,
      }
    }
    const newGen = {
      ...this.props.currentGen,
      tables: newTables
    }
    this.props.contentChanged(JSON.stringify(newGen))
  }

  public addGlobal(tableCount: number, lineCount: number) {
    const newTables = this.props.currentGen.tables;

    if (newTables[tableCount].entries[lineCount].export !== undefined) { // something there!
      newTables[tableCount].entries[lineCount] = {
        ...newTables[tableCount].entries[lineCount],
        export: {
          ...newTables[tableCount].entries[lineCount].export,
          ['new-' + Object.keys(newTables[tableCount].entries[lineCount].export).length]: this.addEmptyGlobal,
        }
      }
    } else {
      if (typeof newTables[tableCount].entries[lineCount] === 'string') {
        newTables[tableCount].entries[lineCount] = { m: 1, v: newTables[tableCount].entries[lineCount] };
      }

      newTables[tableCount].entries[lineCount] = {
        ...newTables[tableCount].entries[lineCount],
        export: this.emptyGlobal,
      }
    }

    const newGen = {
      ...this.props.currentGen,
      tables: newTables
    }

    this.props.contentChanged(JSON.stringify(newGen))
  }

  public addSet(tableCount: number, lineCount: number) {
    const newTables = this.props.currentGen.tables;

    if (newTables[tableCount].entries[lineCount].set !== undefined) {
      newTables[tableCount].entries[lineCount] = {
        ...newTables[tableCount].entries[lineCount],
        set: {
          ...newTables[tableCount].entries[lineCount].set,
          ['new-' + Object.keys(newTables[tableCount].entries[lineCount].set).length]: this.addEmptySet,
        }
      }
    } else {
      if (typeof newTables[tableCount].entries[lineCount] === 'string') {
        newTables[tableCount].entries[lineCount] = { m: 1, v: newTables[tableCount].entries[lineCount] };
      }

      newTables[tableCount].entries[lineCount] = {
        ...newTables[tableCount].entries[lineCount],
        set: this.emptySet,
      }
    }

    const newGen = {
      ...this.props.currentGen,
      tables: newTables
    }

    this.props.contentChanged(JSON.stringify(newGen))
  }

  public addEntry(tableCount: number) {
    const newTables = this.props.currentGen.tables;

    if (newTables[tableCount].entries === undefined) {
      newTables[tableCount] = { ...newTables[tableCount], entries: [] }
    }
    newTables[tableCount].entries.push(
      this.emptyRow
    )
    const newGen = {
      ...this.props.currentGen,
      tables: newTables
    }
    this.props.contentChanged(JSON.stringify(newGen));
    if (this.props.currentGen.tables[tableCount].entries.length % this.state.entriesPerPage === 1) {
      this.setPage(tableCount, Number(this.pageNumber[tableCount] + 1))
    } else {
      this.setPage(tableCount, this.pageNumber[tableCount])
    }
  }

  public addTable = () => {
    let newTables = this.props.currentGen.tables;
    if (newTables === undefined) {
      newTables = []
    }
    newTables.push(
      this.emptyTable
    )
    const newGen = {
      ...this.props.currentGen,
      tables: newTables
    }
    this.props.contentChanged(JSON.stringify(newGen))
  }

  public removeSet(tableCount: number, lineCount: number, setCount: number) {
    const newTables = this.props.currentGen.tables;

    const found = newTables[tableCount].entries[lineCount].set;
    let fCounter = 0;
    let foundObject: string = "";
    // tslint:disable-next-line:forin
    for (const f in found) {
      if (fCounter === setCount) {
        foundObject = f;
        break;
      }
      fCounter++;
    }

    delete newTables[tableCount].entries[lineCount].set[foundObject]

    const newGen = {
      ...this.props.currentGen,
      tables: newTables
    }
    this.props.contentChanged(JSON.stringify(newGen))
  }

  public removeGlobal(tableCount: number, lineCount: number, globalCount: number) {
    const newTables = this.props.currentGen.tables;

    const found = newTables[tableCount].entries[lineCount].export;
    let fCounter = 0;
    let foundObject: string = "";
    // tslint:disable-next-line:forin
    for (const f in found) {
      if (fCounter === globalCount) {
        foundObject = f;
        break;
      }
      fCounter++;
    }

    delete newTables[tableCount].entries[lineCount].export[foundObject]

    const newGen = {
      ...this.props.currentGen,
      tables: newTables
    }
    this.props.contentChanged(JSON.stringify(newGen))
  }

  public removeTableGlobal(tableCount: number, globalCount: number) {

    const newTables = this.props.currentGen.tables;

    const found = newTables[tableCount].export;
    let fCounter = 0;
    let foundObject: string = "";
    // tslint:disable-next-line:forin
    for (const f in found) {
      if (fCounter === globalCount) {
        foundObject = f;
        break;
      }
      fCounter++;
    }

    delete newTables[tableCount].export[foundObject];

    const newGen = {
      ...this.props.currentGen,
      tables: newTables
    }
    this.props.contentChanged(JSON.stringify(newGen))
  }

  public removeEntry(tableCount: number, lineCount: number) {
    const newTables = this.props.currentGen.tables;
    newTables[tableCount].entries.splice(lineCount, 1)
    const newGen = {
      ...this.props.currentGen,
      tables: newTables
    }

    if (this.props.currentGen.tables[tableCount].entries.length % this.state.entriesPerPage === 0) {
      this.tableLengthsUpdated = false
    }

    this.props.contentChanged(JSON.stringify(newGen))
  }

  public removeTable = (tableCount: number) => {
    const newTables = this.props.currentGen.tables;
    newTables.splice(tableCount, 1);
    const newGen = {
      ...this.props.currentGen,
      tables: newTables
    }
    this.props.contentChanged(JSON.stringify(newGen))
  }

  public getSets(set: any, tableCount: number, lineCount: number) {
    const retVal: any[] = [];
    if (set !== undefined) {
      let setCount = 0;
      for (const key in set) {
        if (key !== undefined) {
          const tempNum = setCount;
          if (setCount > 0) {
            retVal.push(
              <div className="field is-grouped" style={{ marginBottom: ".25rem" }} key={tableCount + "-" + lineCount + '-' + setCount}>
                <TextInputSmall label="" value={key} placeholder="Key" changeCallback={(e: React.FormEvent<any>) => this.tableChange(e, 'rowSetKey', tableCount, tempNum, lineCount)} dataField="test" /> {/*tslint:disable-line*/}
                <TextInputSmall label="" value={set[key].v} placeholder="Value" changeCallback={(e: React.FormEvent<any>) => this.tableChange(e, 'rowSetValue', tableCount, tempNum, lineCount)} dataField="test" /> {/*tslint:disable-line*/}
                <div className="field">
                  <button type="button" className="button is-small" onClick={() => this.removeSet(tableCount, lineCount, tempNum)}>-</button> {/*tslint:disable-line*/}
                </div>
              </div>
            );
          } else {
            retVal.push(
              <div className="field is-grouped" style={{ marginBottom: ".25rem" }} key={tableCount + "-" + lineCount + '-' + setCount}>
                <TextInputSmall label="Set Key" value={key} placeholder="Key" changeCallback={(e: React.FormEvent<any>) => this.tableChange(e, 'rowSetKey', tableCount, tempNum, lineCount)} dataField="test" /> {/*tslint:disable-line*/}
                <TextInputSmall label="Set Value" value={set[key].v} placeholder="Value" changeCallback={(e: React.FormEvent<any>) => this.tableChange(e, 'rowSetValue', tableCount, tempNum, lineCount)} dataField="test" /> {/*tslint:disable-line*/}
                <div className="field">
                  <label className="label">&nbsp;</label>
                  <button type="button" className="button is-small" onClick={() => this.removeSet(tableCount, lineCount, tempNum)}>-</button> {/*tslint:disable-line*/}
                </div>
              </div>
            );
          }
        }
        setCount++;
      }
    }

    if (set !== undefined) {
      retVal.push(
        <div className="field is-grouped" key={tableCount + "-" + lineCount}>
          <div className="field" >
            <label className="label" >Key Value Set</label>
            <button type="button" className="button" onClick={() => this.addSet(tableCount, lineCount++)}>+</button> {/*tslint:disable-line*/}
          </div>
        </div>
      );
    } else {
      retVal.push(
        <div className="field is-grouped" key={tableCount + "-" + lineCount}>
          <div className="field">
            <label className="label" >Key Value Set</label>
            <div className="field is-grouped">
              <button type="button" className="button" onClick={() => this.addSet(tableCount, lineCount++)}>+</button> {/*tslint:disable-line*/}
            </div>
          </div>
        </div>
      );
    }

    return retVal;
  }

  public getGlobals(globals: any, tableCount: number, lineCount: number) {
    const retVal: any[] = [];
    if (globals !== undefined && globals !== null) {
      let globalCount = 0;
      for (const key in globals) {
        if (key !== undefined && globals !== null && globals[key] !== null && globals[key] !== undefined) {
          const tempNum = globalCount;
          if (tempNum > 0) {
            retVal.push(
              <div className="field is-grouped" style={{ marginBottom: ".25rem" }} key={tableCount + "-" + lineCount + '-' + globalCount}>
                <TextInputSmall label="" value={key} placeholder="Key" changeCallback={(e: React.FormEvent<any>) => this.tableChange(e, 'rowExportKey', tableCount, tempNum, lineCount)} dataField="test" />{/*tslint:disable-line*/}
                <TextInputSmall label="" value={globals[key].v} placeholder="Value" changeCallback={(e: React.FormEvent<any>) => this.tableChange(e, 'rowExportValue', tableCount, tempNum, lineCount)} dataField="test" />{/*tslint:disable-line*/}
                <div className="field">
                  <button type="button" className="button is-small" onClick={() => this.removeGlobal(tableCount, lineCount, tempNum)}>-</button> {/*tslint:disable-line*/}
                </div>
              </div>
            );

          } else {
            retVal.push(
              <div className="field is-grouped" style={{ marginBottom: ".25rem" }} key={tableCount + "-" + lineCount + '-' + globalCount}>
                <TextInputSmall label="Export Global Key" value={key} placeholder="Key" changeCallback={(e: React.FormEvent<any>) => this.tableChange(e, 'rowExportKey', tableCount, tempNum, lineCount)} dataField="test" />{/*tslint:disable-line*/}
                <TextInputSmall label="Export Global Value" value={globals[key].v} placeholder="Value" changeCallback={(e: React.FormEvent<any>) => this.tableChange(e, 'rowExportValue', tableCount, tempNum, lineCount)} dataField="test" />{/*tslint:disable-line*/}
                <div className="field">
                  <label className="label">&nbsp;</label>
                  <button type="button" className="button is-small" onClick={() => this.removeGlobal(tableCount, lineCount, tempNum)}>-</button> {/*tslint:disable-line*/}
                </div>
              </div>
            );
          }
        }
        globalCount++;
      }
    }

    if (globals !== undefined) {
      retVal.push(
        <div className="field is-grouped" key={"global" + this.elementCounter++}>
          <div className="field" >
            <label className="label" >Key Value Export Globals</label>
            <button type="button" className="button" onClick={() => this.addGlobal(tableCount, lineCount++)}>+</button> {/*tslint:disable-line*/}
          </div>
        </div>
      );
    } else {
      retVal.push(
        <div className="field is-grouped" key={"global" + this.elementCounter++}>
          <div className="field">
            <label className="label" >Key Value Export Globals</label>
            <div className="field is-grouped">
              <button type="button" className="button" onClick={() => this.addGlobal(tableCount, lineCount++)}>+</button> {/*tslint:disable-line*/}
            </div>
          </div>
        </div>
      );
    }

    return retVal;
  }

  public getTableGlobals(globals: any, tableCount: number) {
    const retVal: any[] = [];
    if (globals !== undefined) {
      let globalsCount = 0;
      for (const key in globals) {
        if (key !== undefined) {
          const tempNum = globalsCount;
          if (tempNum > 0) {
            retVal.push(
              <div className="columns is-12" key={'tableglobal-' + tableCount + ' ' + tempNum} >
                <div className="column is-4" style={{ paddingTop: "0", paddingBottom: "0", margin: "8px 0 8px 0" }}>
                  <TextInputSmall label="" value={key} placeholder="Key" changeCallback={(e: React.FormEvent<any>) => this.tableChange(e, 'tableGlobalKey', tableCount, tempNum)} dataField="test" />{/*tslint:disable-line*/}
                </div>
                <div className="column is-8" style={{ paddingTop: "0", paddingBottom: "0", margin: "8px 0 8px 0" }}>
                  <div className="field is-grouped">
                    <TextInputSmall label="" value={globals[key].v} placeholder="Value" changeCallback={(e: React.FormEvent<any>) => this.tableChange(e, 'tableGlobalValue', tableCount, tempNum)} dataField="test" />{/*tslint:disable-line*/}
                    <div className="field" >
                      <button type="button" className="button is-small" onClick={() => this.removeTableGlobal(tableCount, tempNum)}>-</button> {/*tslint:disable-line*/}
                    </div>
                  </div>
                </div>
              </div>
            );
          } else {
            retVal.push(
              <div className="columns is-12" key={'tableglobal-' + tableCount + ' ' + tempNum}>
                <div className="column is-4" style={{ paddingTop: "8px", paddingBottom: "0", margin: "8px 0 8px 0" }}>
                  <TextInputSmall label="Export Global Key" value={key} placeholder="Key" changeCallback={(e: React.FormEvent<any>) => this.tableChange(e, 'tableGlobalKey', tableCount, tempNum)} dataField="test" />{/*tslint:disable-line*/}
                </div>
                <div className="column is-8" style={{ paddingTop: "8px", paddingBottom: "0", margin: "8px 0 8px 0" }}>
                  <div className="field is-grouped">
                    <TextInputSmall label="Export Global Value" value={globals[key].v} placeholder="Value" changeCallback={(e: React.FormEvent<any>) => this.tableChange(e, 'tableGlobalValue', tableCount, tempNum)} dataField="test" />{/*tslint:disable-line*/}
                    <div className="field" >
                      <label className="label">&nbsp;</label>
                      <button type="button" className="button is-small" onClick={() => this.removeTableGlobal(tableCount, tempNum)}>-</button> {/*tslint:disable-line*/}
                    </div>
                  </div>
                </div>
              </div>
            );
          }
        }
        globalsCount++;
      }
    }

    if (globals !== undefined) {
      retVal.push(
        <div className="field is-grouped" key={'tableglobal-' + tableCount}>
          <div className="field" >
            <label className="label" >Key Value Export Globals</label>
            <button type="button" className="button" onClick={() => this.addTableGlobal(tableCount)}>+</button> {/*tslint:disable-line*/}
          </div>
        </div>
      );
    } else {
      retVal.push(
        <div className="field is-grouped" key={'tableglobal-' + tableCount}>
          <div className="field">
            <label className="label" >Key Value Export Globals</label>
            <div className="field is-grouped">
              <button type="button" className="button" onClick={() => this.addTableGlobal(tableCount)}>+</button> {/*tslint:disable-line*/}
            </div>
          </div>
        </div>
      );
    }
    return retVal;
  }

  public updateTableLengths = (entriesPerPage?: number) => {
    if (this.props.currentGen !== undefined && this.props.currentGen !== null) {
      if (this.props.currentGen.tables !== undefined && this.props.currentGen !== null) {
        let newCurrentPages = {};
        for (let i = 0; i < this.props.currentGen.tables.length; i++) { //tslint:disable-line
          newCurrentPages = {
            ...newCurrentPages,
            [i]: 1
          }
        }
        this.tableLengthsUpdated = true;
        this.setState({
          ...this.state,
          currentPages: newCurrentPages,
          entriesPerPage: entriesPerPage || this.state.entriesPerPage
        });
      }
    }
  }

  public getPageCounts = (entries: any, tableNumber: number) => {
    let newTableCount = 1
    if (entries !== undefined) {
      newTableCount = Math.ceil(entries.length / this.state.entriesPerPage);
    }
    this.pageNumber[tableNumber] = newTableCount;
  }

  public getPageEntries(entries: any, p: number) {
    const page = p || 1;
    let newEntries: any[] = [];
    if (entries !== null && entries !== undefined) {
      const start = (this.state.entriesPerPage * page) - this.state.entriesPerPage;
      const end = this.state.entriesPerPage * page;
      newEntries = Array.prototype.slice.call(entries, start, end);
    }
    return newEntries;
  }

  public getEntries(entries: any, tableCount: number, p: number) {
    const page = p || 1;
    const offset: number = (page - 1) * this.state.entriesPerPage;
    let lineCount: number = 0;
    let lineCountGlobal: number = 0;
    const retVal: any[] = [];
    if (entries !== null && entries !== undefined) {
      let LineCountNew = 0;
      for (const originalEntry of entries) {
        const entry = typeof originalEntry === 'string' ? { m: 1, v: originalEntry } : originalEntry;
        if (entry.m === undefined) {
          entry.m = 1;
        }

        const tempNum = LineCountNew + offset;
        retVal.push(
          <div className="box" style={{ backgroundColor: "#f1f2f6", boxShadow: "0px 0px 0px 0px", marginBottom: "0.25rem", padding: "16px" }} key={tableCount + "-" + lineCount}>
            <div className="columns is-12">
              <div className="column is-2">
                <TextInput label="Value V" value={entry.v} placeholder="V" changeCallback={(e: React.FormEvent<any>) => this.tableChange(e, 'entryValue', tableCount, tempNum)} dataField="test" /> {/*tslint:disable-line*/}
                {/* {entries.length > 1 || this.pageNumber[tableCount] > 1 || (entries.length > 0 && this.state.searchEntryStrings[tableCount] !== "") ? <button type="button" className="button is-small is-danger" onClick={() => this.removeEntry(tableCount, tempNum)}>{this.props.intl.formatMessage(translations.deleteRow)}</button> : ''} tslint:disable-line */}
                <button type="button" className="button is-small is-danger" onClick={() => this.removeEntry(tableCount, tempNum)}>{this.props.intl.formatMessage(translations.deleteRow)}</button> {/*tslint:disable-line*/}
              </div>
              <div className="column is-2">
                <TextInput label="Multiplicity M" value={entry.m} placeholder="M" changeCallback={(e: React.FormEvent<any>) => this.tableChange(e, 'entryMultiplicity', tableCount, tempNum)} dataField="test" />{/*tslint:disable-line*/}
              </div>
              <div className="column" style={{ backgroundColor: "#dfe4ea", borderRadius: "6px 0px 0px 6px", paddingTop: "8px", paddingBottom: "8px", margin: "8px 0 8px 0" }}>
                {this.getSets(entry.set, tableCount, lineCount++)}
              </div>
              <div className="column" style={{ backgroundColor: "#dfe4ea", borderRadius: "0px 6px 6px 0px", paddingTop: "8px", paddingBottom: "8px", margin: "8px 8px 8px 0" }}>
                {this.getGlobals(entry.export, tableCount, lineCountGlobal++)}
              </div>
            </div>
          </div>
        );
        LineCountNew++;
      }
    }
    return retVal;
  }

  public setPage = (tableCount: number, page: number) => {
    this.setState({
      ...this.state,
      currentPages: {
        ...this.state.currentPages,
        [tableCount]: page
      }
    })
  }

  public getPaginationArray(pageIndex: number, pageCount: number) {
    if (pageCount < 1) { return [] };

    if (pageIndex < 0) { pageIndex = 0 };
    if (pageIndex > pageCount - 1) { pageIndex = pageCount - 1 };

    const array = [];
    array.push(1);

    if (pageIndex > 2) { array.push('...') };

    if (pageIndex > 1 && pageIndex < pageCount) { array.push(pageIndex) };
    if (pageIndex > 0 && pageIndex < pageCount - 1) { array.push(pageIndex + 1) };
    if (pageIndex > -1 && pageIndex < pageCount - 2) { array.push(pageIndex + 2) };

    if (pageIndex < pageCount - 3) { array.push('...') };

    if (pageCount > 1) { array.push(pageCount) };

    return array;
  }

  public getPaginationInner = (tableCount: number) => {
    const retVal = [];
    const innerPaginationArray = this.getPaginationArray(this.state.currentPages[tableCount] - 1, this.pageNumber[tableCount]);

    for (const page of innerPaginationArray) {
      if (page === "...") {
        retVal.push(<li key={'P_' + tableCount + '_' + page}><span className="pagination-ellipsis is-small" >&hellip;</span></li>);
      } else if (page === this.state.currentPages[tableCount]) {
        retVal.push(<li key={'P_' + tableCount + '_' + page}><a className="pagination-link is-current button is-small" onClick={() => this.setPage(tableCount, Number(page))}>{Number(page)}</a></li>); {/*tslint:disable-line*/ }
      } else {
        retVal.push(<li key={'P_' + tableCount + '_' + page}><a className="pagination-link button is-outlined is-small" key={'P_' + tableCount + '_' + page} onClick={() => this.setPage(tableCount, Number(page))}>{Number(page)}</a></li>); {/*tslint:disable-line*/ }
      }
    }

    return retVal;
  }

  public getPagination = (tableCount: number) => {
    const retVal = [];
    if (this.state.currentPages[tableCount] > 1) {
      retVal.push(<button className="pagination-previous button is-outlined is-small" key={tableCount + '_1'} onClick={() => this.setPage(tableCount, this.state.currentPages[tableCount] - 1)}>{this.props.intl.formatMessage(translations.previous)}</button>); {/*tslint:disable-line*/ }
    } else {
      retVal.push(<button className="pagination-previous button is-outlined is-small" disabled={true} key={tableCount + '_2'}>{this.props.intl.formatMessage(translations.previous)}</button>); {/*tslint:disable-line*/ }
    }
    retVal.push(<ul className="pagination-list" key={tableCount + '_5'}>{this.getPaginationInner(tableCount)}</ul>)
    if (this.state.currentPages[tableCount] < this.pageNumber[tableCount]) {
      retVal.push(<button className="pagination-next button is-outlined is-small" key={tableCount + '_3'} onClick={() => this.setPage(tableCount, Number(this.state.currentPages[tableCount] + 1))}>{this.props.intl.formatMessage(translations.next)}</button>); {/*tslint:disable-line*/ }
    } else {
      retVal.push(<button className="pagination-next button is-outlined is-small" disabled={true} key={tableCount + '_4'} >{this.props.intl.formatMessage(translations.next)}</button>); {/*tslint:disable-line*/ }
    }
    return retVal;
  }

  public handleEntrySearch = (tableCount: number, e: any) => {
    const curValue = e.currentTarget.value;
    this.setPage(tableCount, 1)
    this.setState({
      searchEntryStrings: {
        ...this.state.searchEntryStrings,
        [tableCount]: curValue
      }
    })
  }

  public filterEntries = (entries: any, tableCount: number) => {
    const newEntries = [];
    const searchString = this.state.searchEntryStrings[tableCount];
    if (searchString !== undefined && searchString !== "") {
      for (const entry of entries) {
        let found = false;
        // Catch double * error
        if (entry.v.toLowerCase().includes(searchString.toLowerCase())) {
          found = true;
        }
        if (found) { newEntries.push(entry) }
      }
      return newEntries;
    } else {
      return entries;
    }
  }

  public getTable(name: any, explanation: any, entries: any, tableCount: number, globals: any) {

    if (name === undefined || null) {
      name = "[name]";
    }
    if (explanation === undefined || null) {
      explanation = "[explanation]";
    }
    return (
      <div key={btoa('table-' + tableCount)}>
        <Collapsible
          trigger={
            <h5 className="title is-5">
              <span className="icon">
                <i className="fas fa-caret-right" />{" "}
              </span>
              <a>{name !== null ? name : "undefined"}</a>
            </h5>
          }
          triggerWhenOpen={
            <h5 className="title is-5">
              <span className="icon">
                <i className="fas fa-caret-down" />
              </span>
              <a>{name !== null ? name : "undefined"}</a>
            </h5>
          }
          key={'table-' + tableCount + 'c'}
        >
          <div className="field" >
            <div className="control" >
              <br />
              <div className="columns is-12">
                <div className="column is-6">
                  <TextInput label="Name" value={name} placeholder="NAME" changeCallback={(e: React.FormEvent<any>) => this.tableChange(e, 'tableName', tableCount)} dataField="test" />  {/*tslint:disable-line*/}
                  <br />
                  <div className="field">
                    <label className="label">Export Globals:</label>
                  </div>
                </div>
                <div className="column is-6">
                  <TextInput label="Explanation" value={explanation} placeholder="EXPLANATION" changeCallback={(e: React.FormEvent<any>) => this.tableChange(e, 'tableExplanation', tableCount)} dataField="test" />  {/*tslint:disable-line*/}
                  <br />
                  <div className="buttons has-addons is-right">
                    <a className="button is-small is-danger" onClick={() => this.removeTable(tableCount)}>{this.props.intl.formatMessage(translations.deleteTable)}</a> {/* tslint:disable-line */}
                  </div>
                </div>
              </div>
              <div className="box" style={{ backgroundColor: "#f1f2f6", boxShadow: "0px 0px 0px 0px", marginBottom: "0.25rem", padding: "16px" }}>
                {this.getTableGlobals(globals, tableCount)}
              </div>
              <br />
              <div className="columns is-12">
                <div className="column is-8">
                  <label className="label">{this.props.intl.formatMessage(translations.entries)}:</label>
                </div>
                <div className="column is-4 is-right">
                  <div className="field is-grouped">
                    <input className="input is-small" type="text" placeholder="Search..." value={this.state.searchEntryStrings[tableCount] === undefined ? '' : this.state.searchEntryStrings[tableCount]} onChange={(e) => this.handleEntrySearch(tableCount, e)} /> {/* tslint:disable-line */}
                  </div>
                </div>
              </div>

              {this.getPageCounts(this.filterEntries(entries, tableCount), tableCount)}

              {this.getEntries(this.getPageEntries(this.filterEntries(entries, tableCount), this.state.currentPages[tableCount]), tableCount, this.state.currentPages[tableCount])}

              <div className="field is-grouped">
                <button type="button" className="button is-small" onClick={() => this.addEntry(tableCount++)} style={{ marginTop: "1rem" }} >+ {this.props.intl.formatMessage(translations.addNewRow)}</button> {/*tslint:disable-line*/}
              </div>
              {/* <br /> */}
              {this.pageNumber[tableCount] > 1 &&
                <nav className="pagination is-right" role="navigation">
                  {this.getPagination(tableCount)}
                </nav>
              }
              <hr />
            </div>
          </div>
        </Collapsible>
      </div>
    );
  }

  public tableToForm() {
    const retVal = []
    let tableCount: number = 0;
    if (this.props.currentGen.tables !== undefined && this.props.currentGen.tables !== null) {
      for (const table of this.props.currentGen.tables) {
        retVal.push(this.getTable(table.name, table.explanation, table.entries, tableCount++, table.export));
        retVal.push(<br key={this.elementCounter++} />);
      }
    }
    retVal.push(<button type="button" className={"button"} key={this.elementCounter++} onClick={this.addTable}>+ {this.props.intl.formatMessage(translations.addNewTable)}</button>); {/*tslint:disable-line*/ }
    return retVal;
  }

  public goToCodeEditor = () => {
    this.props.switchView(preferredEditor.codeEditor);
    Log(logType.action, logAction.clickHandler, "FormGeneratorComponent", "Pressed the changed to Code Editor button!");
  }

  public importAllImages = () => {
    const context = require.context("../../../assets/cards/", true);

    context.keys().forEach((key: any) => {
      const countryCode = key
        .split("./")
        .pop() // remove the first 2 characters
        .substring(0, key.length - 6); // remove the file extension
      this.obj[countryCode] = context(key);
    });

    // return obj;
  }

  public findImage: any = (categories: any) => {
    if (categories !== null && categories.length > 0) {
      // We've got categories!
      this.importAllImages();
      const imageList = Object.keys(this.obj);
      // We've got all images!
      const genName: string = this.props.currentGen.name;
      const genExplanation: any = this.props.currentGen.explanation;

      for (const img of imageList) {
        if (genName.toLowerCase().search(img.toLowerCase()) !== -1) {
          return this.obj[img];
        }
      }

      for (const category of categories) {
        for (const img of imageList) {
          if (category.toLowerCase().indexOf(img.toLowerCase()) !== -1) {
            return this.obj[img];
          }
        }
      }
      if (genExplanation !== null && genExplanation !== undefined) {
        for (const img of imageList) {
          if (genExplanation.toLowerCase().search(img.toLowerCase()) !== -1) {
            return this.obj[img];
          }
        }
      }
      return StandardImage;
    }
    return StandardImage;
  }

  public setEntryCountPerPage = (count: number) => {
    this.updateTableLengths(count);
  }

  public render() {
    this.tableToForm = this.tableToForm.bind(this);

    return (
      <>
        <nav className="level">
          <div className="level-left">
            <div className="level-item">
              <nav className="breadcrumb is-fixed-top">
                <ul>
                  <li>
                    <Link to="/generators">
                      <FormattedMessage {...translations.generators} />
                    </Link>
                  </li>
                  {this.props.id === "new" ? (
                    <li className="is-active">
                      <a href="new-book.html">
                        <FormattedMessage {...translations.newgenerator} />
                      </a>
                    </li>
                  ) : (
                    <li className="is-active">
                      <a>{this.props.currentGen.name ? shortenTo(this.props.currentGen.name, 31) : this.props.intl.formatMessage(translations.isLoading)}</a>
                    </li>
                  )}
                </ul>
              </nav>
            </div>
          </div>
          <div className="level-right">
            <div className="level-item">
              <button type="button" className="button" disabled={!this.props.validated} onClick={this.goToCodeEditor}>
                {this.props.intl.formatMessage(translations.switchToCode)}
              </button>
            </div>
          </div>
        </nav>
        {/* Edit Form */}
        {typeof this.props.currentGen !== 'object' &&
          <div className="box is-loading">
            <label className="label">{this.props.intl.formatMessage(translations.isLoading)}</label>
          </div>
        }
        {typeof this.props.currentGen === 'object' &&
          <div className="box">
            <div className="columns is-12">
              <div className="column is-11">
                {/* Name Input */}
                <TextInput // label="A generator name"
                  label={<FormattedMessage {...translations.editname} />}
                  value={this.props.currentGen.name}
                  placeholder="A creative generator name"
                  icon="fa-edit"
                  changeCallback={this.changeHandler}
                  dataField="name"
                />
              </div>
              <div className="column is-1 is-center" style={{ padding: "6px", marginRight: '2px' }}>
                {/* <div className="media-center"> */}
                <img src={this.props.currentGen.categories !== undefined ? this.findImage(this.props.currentGen.categories) : ''} width="96px" height="96px" />
                {/* </div> */}
              </div>
            </div>
            {/* Descrtiprion Field */}
            <AreaInput label={this.props.intl.formatMessage(translations.description)} value={this.props.currentGen.explanation} placeholder="Describe your generator" changeCallback={this.changeHandler} dataField="explanation" />
            {/* Generator Path */}
            <TextInput label={this.props.intl.formatMessage(translations.path)} value={this.props.currentGen.path} placeholder="e.g. /Races/underwater/mammal" changeCallback={this.changeHandler} dataField="path" />
            {/* Categories */}
            <label className="label">{this.props.intl.formatMessage(translations.categories)}</label>
            <ReactTagsInput inputProps={{ className: "react-tagsinput-input tagsinput-width", placeholder: this.props.intl.formatMessage(translations.addATag) }} value={this.props.currentGen.categories !== undefined ? this.props.currentGen.categories : []} onChange={this.handleTagChange} onlyUnique={true} />
            <br />
            {/* Result Pattern */}
            <TextInput
              label={this.props.intl.formatMessage(translations.resultPattern)}
              value={this.props.currentGen.resultPattern}
              placeholder="Result Pattern e.g. || Name: || {name}"
              changeCallback={this.changeHandler}
              dataField="result-pattern"
            />
            <br />

            {/* Tables */}

            <div className="columns is-12">
              <div className="column is-6">
                <p className="subtitle is-4">{this.props.intl.formatMessage(translations.tables)}</p>
              </div>
              <div className="column is-6 is-right">
                <div className="buttons has-addons is-right">
                  <span className="label is-left is-small" style={{ marginRight: '1em' }}>{this.props.intl.formatMessage(translations.entriesPerPage)}: </span>
                  <span className={this.state.entriesPerPage === 10 ? "button is-small is-link" : "button is-small"} onClick={() => this.setEntryCountPerPage(10)}>10</span> {/*tslint:disable-line */}
                  <span className={this.state.entriesPerPage === 25 ? "button is-small is-link" : "button is-small"} onClick={() => this.setEntryCountPerPage(25)}>25</span> {/*tslint:disable-line */}
                  <span className={this.state.entriesPerPage === 50 ? "button is-small is-link" : "button is-small"} onClick={() => this.setEntryCountPerPage(50)}>50</span> {/*tslint:disable-line */}
                  <span className={this.state.entriesPerPage === 100 ? "button is-small is-link" : "button is-small"} onClick={() => this.setEntryCountPerPage(100)}>100</span> {/*tslint:disable-line */}
                </div>
              </div>
            </div>

            {this.tableToForm()}

            <hr />

            {this.props.syntaxError || this.props.validationError
              ?
              <article className="message is-danger">
                <div className="message-body" dangerouslySetInnerHTML={{ __html: this.props.errorMessage }} />
              </article>
              : ''}

            <nav className="level">
              {/* Run & Validate */}
              <div className="level-left">
                <div className="level-item">
                  <button className="button is-dark is-outlined" onClick={this.props.toggleRunView}>
                    <span className="icon">
                      <i className="far fa-play-circle" />
                    </span>
                    <span>{this.props.isShowingRunWindow ? this.props.intl.formatMessage(translations.hideRunWindow) : this.props.intl.formatMessage(translations.showRunWindow)}</span>
                  </button>
                </div>
                <div className="level-item">
                  <button className={"button is-dark is-outlined " + (this.props.isLoadingValidation ? 'is-loading' : '')} disabled={this.props.syntaxError} onClick={this.props.validateGenerator}>
                    <span className="icon">
                      <i className="far fa-check-circle" />
                    </span>
                    <span>{this.props.intl.formatMessage(translations.validate)}</span>
                  </button>
                </div>
                {this.props.isShowingRunWindow &&
                  <div className="level-item">
                    <p className="buttons">
                      <button className="button is-warning is-outlined" onClick={this.goBack} >
                        <span className="icon is-small">
                          <i className="far fa-times-circle" />
                        </span>
                        <span>{this.props.intl.formatMessage(translations.exit)}</span>
                      </button>
                      <button className={"button is-success " + this.state.isLoadingSaving} onClick={this.props.saveGenerator}>
                        <span className="icon is-small">
                          <i className="fas fa-check-double" />
                        </span>
                        <span>{this.props.intl.formatMessage(translations.save)}</span>
                      </button>
                    </p>
                  </div>
                }
              </div>
              {/* Cancel & Save */}
              <div className="level-right">
                <div className="level-item">
                  <p className="buttons">
                    <button className="button is-warning is-outlined" onClick={this.goBack} >
                      <span className="icon is-small">
                        <i className="far fa-times-circle" />
                      </span>
                      <span>{this.props.intl.formatMessage(translations.exit)}</span>
                    </button>
                    <button className={"button is-success " + this.state.isLoadingSaving} onClick={this.props.saveGenerator}>
                      <span className="icon is-small">
                        <i className="fas fa-check-double" />
                      </span>
                      <span>{this.props.intl.formatMessage(translations.save)}</span>
                    </button>
                  </p>
                </div>
              </div>

            </nav>
          </div>
        }
      </>
    );
  }
}

export default injectIntl(FormGeneratorComponent)



// WEBPACK FOOTER //
// ./src/Containers/generators/EditGenerator/FormGeneratorComponent.tsx